import Phaser from "phaser";
import { playActorAnimation } from "../systems/actor-animations.js";
import { useEmergencyPotion } from "../systems/consumable-system.js";
import { startKnockback, updateKnockback } from "../systems/knockback.js";
import {
  ATTACK_INPUT_BUFFER_MS,
  DODGE_INPUT_BUFFER_MS,
  canDodgeCancelAttack,
  getAssistedAttackFacing,
  getAttackMoveMultiplier,
  getMoveIntent,
} from "../systems/player-control.js";

const EPSILON = 0.001;

export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, options = {}) {
    const level = Math.max(1, Math.min(3, Number(options.level) || 1));
    const assetId = level === 1 ? "player" : `player_lvl${level}`;
    const texture = level === 1 ? "provided-player" : `provided-player-lvl${level}`;
    super(scene, x, y, texture);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setScale(2);
    this.setDepth(10);
    this.setCollideWorldBounds(true);
    this.body.setSize(12, 8).setOffset(26, 28);
    this.body.setDrag(900, 900);
    this.body.setMaxVelocity(230, 230);

    this.maxHealth = 100;
    this.visualLevel = level;
    this.assetId = assetId;
    this.health = 100;
    this.attackDamage = 20;
    this.attackRange = 88;
    this.attackArcDeg = 100;
    this.moveSpeed = 192;
    this.attackCooldownMs = 360;
    this.attackCooldownRemaining = 0;
    this.attackBufferRemaining = 0;
    this.attackRemaining = 0;
    this.attackElapsed = 0;
    this.attackLungeRemaining = 0;
    this.attackStarted = false;
    this.attackHitWindow = false;
    this.attackHitResolved = false;
    this.attackAnimationState = "attack";
    this.dodgeCooldownMs = 1000;
    this.dodgeCooldownRemaining = 0;
    this.dodgeBufferRemaining = 0;
    this.dodgeRemaining = 0;
    this.dodgeChainRemaining = 0;
    this.hurtAnimationRemaining = 0;
    this.deathAnimationPlayed = false;
    this.invulnerableRemaining = 0;
    this.knockbackRemaining = 0;
    this.knockbackVelocityX = 0;
    this.knockbackVelocityY = 0;
    this.damageReduction = 0;
    this.knockbackMultiplier = 1;
    this.bleedDamage = 0;
    this.lifestealAmount = 0;
    this.lifestealTriggers = 0;
    this.machineResonanceStacks = 0;
    this.machineDamageMultiplier = 1;
    this.comboDrive = false;
    this.comboHits = 0;
    this.lastStand = false;
    this.gold = 0;
    this.consumables = 0;
    this.trophy = false;
    this.buffs = [];
    this.buffStacks = {};
    this.facing = new Phaser.Math.Vector2(1, 0);
    this.attackFacing = this.facing.clone();
    this.shadowOffsetY = 22;
    this.shadow = scene.add.image(x, y + this.shadowOffsetY, "provided-shadow").setScale(2).setAlpha(0.62).setDepth(5);
    playActorAnimation(this, this.assetId, "idle", this.facing);
  }

  updateActor(input, delta) {
    const dt = Math.max(0, delta);
    this.attackStarted = false;
    this.attackHitWindow = false;
    this.attackCooldownRemaining = Math.max(0, this.attackCooldownRemaining - dt);
    this.attackBufferRemaining = Math.max(0, this.attackBufferRemaining - dt);
    this.dodgeBufferRemaining = Math.max(0, this.dodgeBufferRemaining - dt);
    this.attackLungeRemaining = Math.max(0, this.attackLungeRemaining - dt);
    this.dodgeCooldownRemaining = Math.max(0, this.dodgeCooldownRemaining - dt);
    this.dodgeChainRemaining = Math.max(0, this.dodgeChainRemaining - dt);
    this.hurtAnimationRemaining = Math.max(0, this.hurtAnimationRemaining - dt);
    this.invulnerableRemaining = Math.max(0, this.invulnerableRemaining - dt);
    if (input.attack) this.attackBufferRemaining = ATTACK_INPUT_BUFFER_MS;
    if (input.dodge) this.dodgeBufferRemaining = DODGE_INPUT_BUFFER_MS;

    if (this.health <= 0) {
      this.attackBufferRemaining = 0;
      this.dodgeBufferRemaining = 0;
      this.setVelocity(0, 0);
      this.updateVisuals();
      return;
    }

    if (this.dodgeRemaining > 0) {
      this.dodgeRemaining = Math.max(0, this.dodgeRemaining - dt);
      if (this.dodgeRemaining === 0) {
        this.setVelocity(0, 0);
        this.dodgeChainRemaining = 420;
      }
      this.updateVisuals();
      return;
    }

    if (updateKnockback(this, dt)) {
      this.updateVisuals();
      return;
    }

    const intent = getMoveIntent(input.moveX, input.moveY);
    const move = new Phaser.Math.Vector2(intent.x, intent.y);
    if (intent.magnitude > EPSILON) {
      this.facing.copy(move);
      const speed = this.moveSpeed * intent.magnitude * getAttackMoveMultiplier(this);
      this.setVelocity(move.x * speed, move.y * speed);
    } else if (this.attackLungeRemaining > 0) {
      this.setVelocity(this.attackFacing.x * 92, this.attackFacing.y * 92);
    } else {
      this.setVelocity(0, 0);
    }

    if (this.dodgeBufferRemaining > 0 && this.tryDodge(intent.magnitude > EPSILON ? move : this.facing)) {
      this.dodgeBufferRemaining = 0;
      this.updateVisuals();
      return;
    }
    if (this.attackBufferRemaining > 0 && this.tryAttack()) this.attackBufferRemaining = 0;

    if (this.attackRemaining > 0) {
      this.attackRemaining = Math.max(0, this.attackRemaining - dt);
      this.attackElapsed += dt;
      if (!this.attackHitResolved && this.attackElapsed >= 82) {
        this.attackHitResolved = true;
        this.attackHitWindow = true;
      }
    }
    this.updateVisuals();
  }

  tryAttack() {
    if (this.attackCooldownRemaining > 0 || this.dodgeRemaining > 0 || this.hurtAnimationRemaining > 0 || this.health <= 0) return false;
    this.attackCooldownRemaining = this.attackCooldownMs;
    this.attackRemaining = 220;
    this.attackElapsed = 0;
    this.attackLungeRemaining = 86;
    this.attackStarted = true;
    this.attackHitResolved = false;
    const assistedFacing = getAssistedAttackFacing(this, this.getMeleeTargets?.() || []);
    this.attackFacing.set(assistedFacing.x, assistedFacing.y);
    const moving = this.body.velocity.lengthSq() > 16;
    this.attackAnimationState = this.dodgeChainRemaining > 0 ? "runAttack" : moving ? "walkAttack" : "attack";
    this.dodgeChainRemaining = 0;
    if (!moving) this.setVelocity(this.attackFacing.x * 92, this.attackFacing.y * 92);
    this.scene.audio?.beep("attack");
    return true;
  }

  tryDodge(direction) {
    if (this.dodgeCooldownRemaining > 0 || this.dodgeRemaining > 0 || this.hurtAnimationRemaining > 0 || this.health <= 0) return false;
    if (!canDodgeCancelAttack(this)) return false;
    this.dodgeCooldownRemaining = this.dodgeCooldownMs;
    this.dodgeRemaining = 220;
    this.invulnerableRemaining = 240;
    this.attackRemaining = 0;
    this.attackLungeRemaining = 0;
    this.attackHitWindow = false;
    this.attackHitResolved = true;
    this.setVelocity(direction.x * 500, direction.y * 500);
    this.scene.audio?.beep("dodge");
    return true;
  }

  takeDamage(amount, context = {}) {
    if (this.invulnerableRemaining > 0 || this.health <= 0) return false;
    const dealt = Math.max(1, Math.round(amount - this.damageReduction));
    this.health = Math.max(0, this.health - dealt);
    this.invulnerableRemaining = 600;
    if (context.knockback) startKnockback(this, context.knockback, { distance: 14, durationMs: 110 });
    this.attackRemaining = 0;
    this.attackElapsed = 0;
    this.attackLungeRemaining = 0;
    this.attackBufferRemaining = 0;
    this.attackHitWindow = false;
    this.attackHitResolved = true;
    this.hurtAnimationRemaining = this.health > 0 ? 230 : 0;
    this.scene.runStats && (this.scene.runStats.damageTaken += dealt);
    this.scene.onPlayerDamaged?.(dealt);
    this.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
    this.scene.time.delayedCall(72, () => this.active && this.setTint(0xffffff).setTintMode(Phaser.TintModes.MULTIPLY));
    playActorAnimation(this, this.assetId, this.health > 0 ? "hurt" : "death", this.facing, { restart: true });
    if (this.health <= 0) this.deathAnimationPlayed = true;
    return true;
  }

  consumePotion() {
    const result = useEmergencyPotion(this);
    this.scene.showStatus?.(result.message);
    if (!result.used) {
      this.scene.audio?.beep("ui");
      return false;
    }
    this.scene.runStats && (this.scene.runStats.consumablesUsed += 1);
    this.scene.audio?.beep("reward");
    return true;
  }

  isAttacking() {
    return this.attackRemaining > 0;
  }

  isDodging() {
    return this.dodgeRemaining > 0;
  }

  updateVisuals() {
    this.setAlpha(this.health > 0 && this.invulnerableRemaining > 0 && Math.floor(this.scene.time.now / 70) % 2 === 0 ? 0.35 : 1);
    const visualFacing = this.isAttacking() ? this.attackFacing : this.facing;
    if (this.health <= 0) {
      if (!this.deathAnimationPlayed) {
        playActorAnimation(this, this.assetId, "death", visualFacing, { restart: true });
        this.deathAnimationPlayed = true;
      }
    } else if (this.hurtAnimationRemaining > 0) {
      playActorAnimation(this, this.assetId, "hurt", visualFacing);
    } else if (this.attackStarted) playActorAnimation(this, this.assetId, this.attackAnimationState, visualFacing, { restart: true });
    else if (!this.isAttacking()) {
      const moving = this.isDodging() || this.body.velocity.lengthSq() > 16;
      playActorAnimation(this, this.assetId, this.isDodging() ? "run" : moving ? "walk" : "idle", visualFacing);
    }
    this.shadow.setPosition(this.x, this.y + this.shadowOffsetY).setVisible(this.active);
    this.setDepth(10 + this.y / 10000);
  }

  destroy(fromScene) {
    this.shadow?.destroy();
    super.destroy(fromScene);
  }
}
